/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable no-unused-vars */
import { useMemo, useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import dayjs from 'dayjs';
import { Check, X } from 'react-feather';
import { Button, Tag, Modal, notification } from 'antd';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { useDispatch, useSelector } from 'react-redux';
import { ActionDeletePatient } from '../../store/Actions/PatientAction';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import AdvancedTable from '../../components/AdvancedTable';
import { useTablePermissions } from '../../components/AdvancedTable/hooks/useTablePermissions';
import NetworkClient from '../../utils/NetworkClient';
import { BASE_URL } from '../../utils/globals';

const PatientList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = queryString.parse(location.search);
  const userDetails = useSelector(state => state.auth.userDetails);
  const { canCreate, canUpdate, canDelete } = useTablePermissions('patients', userDetails);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleDelete = useCallback((id) => {
    const modal = Modal.confirm({
      title: 'Are you sure you want to delete this patient?',
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        dispatch(
          ActionDeletePatient(id, (res) => {
            if (res?.success || res?.status === 200 || res?.statusCode === 200 || res?.message === 'Customer deleted successfully') {
              notification.success({
                message: 'Success',
                description: 'Patient deleted successfully',
              });
              setRefreshKey((prev) => prev + 1);
            } else {
              notification.error({
                message: 'Error',
                description: res?.message || 'Failed to delete patient',
              });
            }
            modal.update({ okButtonProps: { loading: false } }); // Reset loading just in case
            modal.destroy();
          })
        );
      },
    });
  }, [dispatch]);

  // Fetch data function for AdvancedTable (server-side)
  const fetchPatients = useCallback(async ({ page, perPage, sortField, sortOrder, search, ...filters }) => {
    try {
      let query = `?page=${page}&limit=${perPage}`;

      // Add search (global search uses name)
      if (search) {
        query += `&name=${encodeURIComponent(search)}`;
      }

      // Add filters
      if (filters.name && !search) query += `&name=${encodeURIComponent(filters.name)}`;
      if (filters.phoneNo) query += `&phoneNo=${encodeURIComponent(filters.phoneNo)}`;
      if (filters.isMemberShip !== undefined && filters.isMemberShip !== null && filters.isMemberShip !== '') {
        query += `&isMemberShip=${filters.isMemberShip}`;
      }

      // Add sorting
      if (sortField) {
        query += `&sortBy=${sortField}&sortOrder=${sortOrder === 'asc' ? 1 : -1}`;
      }

      const response = await NetworkClient.get(`${BASE_URL}admin/customers${query}`);

      return {
        data: response?.data?.data?.docs || [],
        total: response?.data?.data?.totalDocs || 0,
      };
    } catch (error) {
      console.error('Error fetching patients:', error);
      return { data: [], total: 0 };
    }
  }, [refreshKey]);

  // Column definitions for AdvancedTable
  const columns = useMemo(() => [
    {
      id: 'name',
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      cell: row => (
        <Link
          to={`/patient-list/patient-profile/${row._id}?page=${params?.page || 1}`}
          style={{ color: '#2e37a4', fontWeight: 500 }}
        >
          {row.name || '---'}
        </Link>
      ),
    },
    {
      id: 'registrationNo',
      name: 'UHID',
      selector: row => row.registrationNo,
      sortable: true,
      width: '120px',
    },
    {
      id: 'phoneNo',
      name: 'Phone No',
      selector: row => row.phoneNo,
      sortable: true,
      width: '140px',
      cell: row => (`${row.countryCode} ${row.phoneNo}`),
    },
    {
      id: 'isMemberShip',
      name: 'Membership',
      width: '120px',
      cell: row => (
        row.isMemberShip
          ? <Tag color="success" icon={<Check size={12} />}>Active</Tag>
          : <Tag color="default" icon={<X size={12} />}>None</Tag>
      ),
    },
    {
      id: 'membershipVerificationStatus',
      name: 'Membership Verification Status',
      selector: row => row.membershipVerificationStatus,
      sortable: true,
      width: '160px',
      cell: row => {
        const status = row.membershipVerificationStatus;
        if (status === 'hidden') {
          return 'Unverified';
        }
        return status || '---';
      },
    },
    {
      id: 'birthDate',
      name: 'Birth Date',
      selector: row => row.birthDate,
      sortable: true,
      width: '120px',
      cell: row => (
        <span>{row.birthDate ? dayjs(row.birthDate).format('DD-MM-YYYY') : '---'}</span>
      ),
    },
    {
      id: 'createdAt',
      name: 'Created At',
      selector: row => row.createdAt,
      sortable: true,
      width: '160px',
      cell: row => (
        <span>{row.createdAt ? dayjs(row.createdAt).format('DD-MM-YYYY HH:mm') : '---'}</span>
      ),
    },
    {
      id: 'actions',
      name: 'Actions',
      width: '180px',
      cell: row => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {canUpdate && (
            <Button
              type="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/20minform?id=${row._id || row.id}&update=true`);
              }}
              style={{
                background: '#2e37a4',
                borderColor: '#2e37a4',
                fontSize: '12px',
              }}
            >
              Health Assessment
            </Button>
          )}

          {canDelete && (
            <Button
              type="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row._id || row.id);
              }}
              style={{
                background: '#e4423cff',
                borderColor: '#e4423cff',
                fontSize: '12px',
              }}
            >
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ], [params?.page, navigate, handleDelete, canUpdate, canDelete]);

  // Filter configuration
  const filterConfig = useMemo(() => [
    {
      id: 'name',
      label: 'Name',
      type: 'text',
      placeholder: 'Search by name...',
    },
    {
      id: 'phoneNo',
      label: 'Phone Number',
      type: 'text',
      placeholder: 'Search by phone...',
    },
    // {
    //   id: 'isMemberShip',
    //   label: 'Membership',
    //   type: 'select',
    //   options: [
    //     { value: 'true', label: 'Active Members' },
    //     { value: 'false', label: 'Non-Members' },
    //   ],
    // },
  ], []);

  // Handle row click
  const handleRowClick = useCallback((row) => {
    navigate(`/patient-list/patient-profile/${row._id}?page=${params?.page || 1}`);
  }, [navigate, params?.page]);

  return (
    <>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={`/patient-list?page=${params?.page || 1}`}>Patient</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <FeatherIcon icon="chevron-right" size={14} />
                  </li>
                  <li className="breadcrumb-item active">Patient List</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Page Header with Create Button */}
          <div className="row mb-3">
            <div className="col-sm-12 d-flex justify-content-between align-items-center">
              <h4 style={{ margin: 0, fontWeight: 600, color: '#111827' }}>Patient List</h4>
              {canCreate && (
                <Link
                  to={`/patient-list/create-patient?update=false&page=${params?.page || 1}`}
                  className="btn"
                  style={{
                    background: '#2e37a4',
                    color: '#fff',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <FeatherIcon icon="plus" size={16} />
                  Add Patient
                </Link>
              )}
            </div>
          </div>

          {/* Advanced Table with all features */}
          <AdvancedTable
            resource="patients"
            columns={columns}
            fetchData={fetchPatients}

            // Features
            enableSearch
            enableFilters
            enableRefresh
            // enableExport
            enableColumnManager

            // Configuration
            filterConfig={filterConfig}
            defaultPageSize={10}
            exportFileName="patients-export"

            // Callbacks
            onRowClick={handleRowClick}

            // Styling
            highlightOnHover
            stickyHeader
            striped
          />
        </div>
      </div>
    </>
  );
};

export default PatientList;
